/**
 * スタミナ制の永続化 & ビジネスロジック。
 *
 * 設計方針:
 *   - `storage.ts` のラッパを使うことで Capacitor ネイティブ環境でも
 *     Preferences → localStorage のミラーが効く。公開 API は同期で維持する。
 *   - 時間経過による自然回復は「読むたびに計算」する副作用のない
 *     `computeCurrentStamina()` と、永続化を伴う `persistCurrentStamina()` に分離
 *     （Match3 版 `getStaminaData` が read-time に副作用を持つのに対して、
 *     本実装は「1 秒間隔の UI ティック」が書き込みを誘発しないよう分離した）。
 *   - スタミナ消費は `consumeStamina({ mode })` でマルチプレイ対応を先取り。
 *     `mode === 'multi'` の場合は消費せず true を返す（将来対戦で呼び出し側を
 *     書き換える余地を残す）。
 *
 * 参考ルール:
 *   - `.claude/rules/no-silent-fallback.md`
 *   - `.claude/rules/no-hardcode.md`
 *   - `.claude/rules/conventions.md`（日付は UTC 基準）
 */
import {
  MAX_STAMINA_ABSOLUTE,
  STAMINA_DAILY_BONUS,
  STAMINA_DAILY_BONUS_STORAGE_KEY,
  STAMINA_MAX_BASE,
  STAMINA_RECOVERY_INTERVAL_MS,
  STAMINA_STORAGE_KEY,
  type DailyBonusState,
  type StaminaState,
} from '../config/stamina'
import { hydrateStorage, readStringSync, writeString } from './storage'

// ---------------------------------------------------------------------------
// Initialization (Capacitor Preferences → localStorage mirror)
// ---------------------------------------------------------------------------

/**
 * ネイティブ環境で Preferences → localStorage のミラーを完了させる。
 * Web では no-op。`main.tsx` で `initSettingsStorage()` と並列に await すること。
 */
export async function initStaminaStorage(): Promise<void> {
  await hydrateStorage([STAMINA_STORAGE_KEY, STAMINA_DAILY_BONUS_STORAGE_KEY])
}

// ---------------------------------------------------------------------------
// Date helpers (UTC base — conventions.md 準拠)
// ---------------------------------------------------------------------------

/**
 * 今日の日付文字列（UTC ベースの YYYY-MM-DD）。
 *
 * プラットフォーム横断で「日付が変わった」判定をするため UTC で統一する。
 * 表示用にローカルタイムが必要な場合は UI 層で変換する。
 */
function getUtcDateString(): string {
  return new Date().toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------
// Daily bonus (永続化 + 判定)
// ---------------------------------------------------------------------------

function parseDailyBonus(raw: string | null): DailyBonusState {
  if (raw == null) return { lastClaimedDate: '' }
  try {
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as { lastClaimedDate?: unknown }).lastClaimedDate ===
        'string'
    ) {
      return { lastClaimedDate: (parsed as { lastClaimedDate: string }).lastClaimedDate }
    }
    return { lastClaimedDate: '' }
  } catch (err) {
    // fallback:ok — JSON 破損時は「まだ受け取っていない」扱いにする。
    // 握り潰しではなく、破損データを検出した旨を警告ログで残す。
    console.warn('[stamina] daily bonus JSON parse failed:', err)
    return { lastClaimedDate: '' }
  }
}

export function loadDailyBonus(): DailyBonusState {
  return parseDailyBonus(readStringSync(STAMINA_DAILY_BONUS_STORAGE_KEY))
}

function saveDailyBonus(state: DailyBonusState): void {
  writeString(STAMINA_DAILY_BONUS_STORAGE_KEY, JSON.stringify(state))
}

/**
 * 今日（UTC 基準）まだデイリーボーナスを受け取っていないか。
 */
export function canClaimDailyBonus(): boolean {
  const today = getUtcDateString()
  return loadDailyBonus().lastClaimedDate !== today
}

/**
 * 現時点で適用される最大スタミナ。
 *   - デイリーボーナスを今日受け取っていれば MAX_BASE + DAILY_BONUS
 *   - 未受け取りなら MAX_BASE
 */
function computeMaxStamina(): number {
  return canClaimDailyBonus()
    ? STAMINA_MAX_BASE
    : STAMINA_MAX_BASE + STAMINA_DAILY_BONUS
}

// ---------------------------------------------------------------------------
// Stamina state (永続化 + 時間経過による自然回復)
// ---------------------------------------------------------------------------

function parseStamina(raw: string | null): StaminaState | null {
  if (raw == null) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as { amount?: unknown }).amount === 'number' &&
      typeof (parsed as { lastUpdateTime?: unknown }).lastUpdateTime ===
        'number' &&
      typeof (parsed as { maxStamina?: unknown }).maxStamina === 'number'
    ) {
      const obj = parsed as StaminaState
      // 絶対上限を超えた異常値は丸める（外部改竄への防御）。
      const clampedAmount = Math.max(
        0,
        Math.min(obj.amount, MAX_STAMINA_ABSOLUTE),
      )
      const clampedMax = Math.max(
        STAMINA_MAX_BASE,
        Math.min(obj.maxStamina, MAX_STAMINA_ABSOLUTE),
      )
      return {
        amount: clampedAmount,
        lastUpdateTime: obj.lastUpdateTime,
        maxStamina: clampedMax,
      }
    }
    return null
  } catch (err) {
    // fallback:ok — JSON 破損時はデフォルト値で復帰（後段でフル回復扱いになる）。
    console.warn('[stamina] stamina JSON parse failed:', err)
    return null
  }
}

/**
 * スタミナ状態をそのままの形で読み込む（自然回復の計算は行わない）。
 *
 * 未保存 or パース失敗時はその時点のフル回復状態を返す。
 */
export function loadStamina(): StaminaState {
  const parsed = parseStamina(readStringSync(STAMINA_STORAGE_KEY))
  if (parsed != null) return parsed

  const maxStamina = computeMaxStamina()
  return {
    amount: maxStamina,
    lastUpdateTime: Date.now(),
    maxStamina,
  }
}

export function saveStamina(state: StaminaState): void {
  writeString(STAMINA_STORAGE_KEY, JSON.stringify(state))
}

/**
 * 現在の状態から時間経過による自然回復を反映した値を計算する（pure）。
 *
 * 重要: この関数は **storage に書き込まない**。UI タイマー（1 秒ごと）から
 * 呼んでも書き込みが誘発されない設計。書き込みが必要なときは
 * `persistCurrentStamina()` を呼ぶこと。
 *
 * 自然回復ルール:
 *   - `amount < STAMINA_MAX_BASE` の場合のみ時間経過で回復（ベース MAX まで）
 *   - ボーナス分（MAX_BASE 超の部分）は時間回復では回復しない
 *   - 回復タイマーは `lastUpdateTime + STAMINA_RECOVERY_INTERVAL_MS`
 */
export function computeCurrentStamina(base?: StaminaState): {
  amount: number
  maxStamina: number
  nextRecoveryInMs: number
  lastUpdateTime: number
} {
  const state = base ?? loadStamina()
  const now = Date.now()
  const maxStamina = computeMaxStamina()

  let amount = state.amount
  let lastUpdateTime = state.lastUpdateTime

  // ベース MAX までの自然回復。ボーナス分は対象外（`recoveryCap` で制限）。
  const recoveryCap = STAMINA_MAX_BASE
  if (amount < recoveryCap) {
    const elapsed = Math.max(0, now - lastUpdateTime)
    const recovered = Math.floor(elapsed / STAMINA_RECOVERY_INTERVAL_MS)
    if (recovered > 0) {
      const nextAmount = Math.min(recoveryCap, amount + recovered)
      if (nextAmount >= recoveryCap) {
        // ベース MAX に到達した時点でタイマーリセット。
        lastUpdateTime = now
      } else {
        // 回復した分だけ基準時刻を進める（次の回復まで正しい残時間になる）。
        lastUpdateTime = lastUpdateTime + recovered * STAMINA_RECOVERY_INTERVAL_MS
      }
      amount = nextAmount
    }
  }

  // 次の回復までの残ミリ秒（ベース MAX 未満の時のみ意味がある値）。
  let nextRecoveryInMs = 0
  if (amount < recoveryCap) {
    const target = lastUpdateTime + STAMINA_RECOVERY_INTERVAL_MS
    nextRecoveryInMs = Math.max(0, target - now)
  }

  return {
    amount,
    maxStamina,
    nextRecoveryInMs,
    lastUpdateTime,
  }
}

/**
 * 時間経過による自然回復を storage に反映して返す。
 *
 * `consumeStamina()` / `recoverFull()` / `claimDailyBonus()` からの
 * 書き込みタイミングで呼ぶ想定。UI タイマーからは呼ばない。
 */
export function persistCurrentStamina(): StaminaState {
  const result = computeCurrentStamina()
  const next: StaminaState = {
    amount: result.amount,
    lastUpdateTime: result.lastUpdateTime,
    maxStamina: result.maxStamina,
  }
  saveStamina(next)
  return next
}

// ---------------------------------------------------------------------------
// Mutation API
// ---------------------------------------------------------------------------

export type ConsumeMode = 'npc' | 'multi'

export interface ConsumeStaminaOptions {
  /** 消費モード。'multi'（マルチプレイ）の場合は消費しない。 */
  mode?: ConsumeMode
}

/**
 * スタミナを 1 消費する。
 *
 * @returns 消費成功した（ゲームを開始してよい）か
 *
 * - `mode === 'multi'` の場合は消費せず必ず true を返す
 *   （将来のリアルタイム対戦では消費しない設計）
 * - `mode === 'npc'` or 未指定の場合は 1 消費。0 なら false
 */
export function consumeStamina(options?: ConsumeStaminaOptions): boolean {
  const mode: ConsumeMode = options?.mode ?? 'npc'
  if (mode === 'multi') return true

  const current = persistCurrentStamina()
  if (current.amount <= 0) return false

  const now = Date.now()
  // MAX から減らす瞬間にタイマーを開始（等倍回復のため今をスタート基準にする）。
  // それ以外は既存の lastUpdateTime を維持（連続消費で残時間がリセットされない）。
  const nextLastUpdate =
    current.amount === current.maxStamina ? now : current.lastUpdateTime

  const next: StaminaState = {
    amount: current.amount - 1,
    lastUpdateTime: nextLastUpdate,
    maxStamina: current.maxStamina,
  }
  saveStamina(next)
  return true
}

/**
 * 現在の最大スタミナ（ボーナス込み）までフル回復する。
 *
 * 広告視聴成功後などに呼ぶ。回復タイマーは即時リセット。
 */
export function recoverFull(): void {
  const maxStamina = computeMaxStamina()
  const next: StaminaState = {
    amount: maxStamina,
    lastUpdateTime: Date.now(),
    maxStamina,
  }
  saveStamina(next)
}

/**
 * デイリーボーナスを受け取る。
 *
 *   - 受け取り済み（今日の UTC 日付）なら false
 *   - 未受け取りなら `lastClaimedDate` を今日にセット、スタミナも +DAILY_BONUS
 *     （上限はボーナス込み MAX = MAX_BASE + DAILY_BONUS）
 */
export function claimDailyBonus(): boolean {
  if (!canClaimDailyBonus()) return false

  // 先にボーナス受け取り状態を書き換えてから最大スタミナを再計算する。
  saveDailyBonus({ lastClaimedDate: getUtcDateString() })

  // ボーナス込み MAX に基づいて加算。先に自然回復を反映してから +5 する。
  const current = persistCurrentStamina()
  const newMax = STAMINA_MAX_BASE + STAMINA_DAILY_BONUS
  const nextAmount = Math.min(newMax, current.amount + STAMINA_DAILY_BONUS)

  const next: StaminaState = {
    amount: nextAmount,
    lastUpdateTime: Date.now(),
    maxStamina: newMax,
  }
  saveStamina(next)
  return true
}

/**
 * デバッグ / 開発用: スタミナ状態を初期化する。
 * 本番 UI には露出させない想定だが、手動検証のために export しておく。
 */
export function resetStamina(): void {
  const maxStamina = computeMaxStamina()
  saveStamina({
    amount: maxStamina,
    lastUpdateTime: Date.now(),
    maxStamina,
  })
}
